import { getConsent } from "./utils/consentStore";
import ContentNoticePage from "./pages/legal/content-notice";
import { telemetryPath, telemetryReferrer } from "./utils/telemetryPrivacy";
import { Authenticated, Refine, ResourceProps, usePermissions, useGo, useGetIdentity } from "@refinedev/core";
import {
    CatchAllNavigate,
    DocumentTitleHandler,
    NavigateToResource,
    UnsavedChangesNotifier,
} from "@refinedev/react-router";
import { dataProvider } from "./customDataProvider";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useSearchParams, useNavigate } from "react-router";
import { authProvider, TOKEN_KEY } from "./authProvider";
import routerBindings from "@refinedev/react-router";

import { Box, CssBaseline, GlobalStyles, Skeleton, Typography, alpha } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useEffect, useState, useCallback } from "react";
import { ErrorPage } from "./pages/error";
import { APP_CONFIG } from "./constants";
import { ColorModeContextProvider } from "./contexts/color-mode";
import { LanguageProvider } from "./contexts/LanguageContext";
import { TourProvider } from "./contexts/TourContext";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { SignalRProvider } from "./contexts/SignalRContext";
import { ToastProvider, useToast } from "./contexts/ToastContext";
import { AppMenuResourcesProvider } from "./contexts/AppMenuResourcesContext";
import { useNotifications } from "./hooks/useNotifications";
import { useConsentSync } from "./hooks/useConsentSync";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { trackEvent, ANALYTICS_EVENTS } from "./utils/analytics";
import { captureFirstTouch } from "./utils/firstTouch";
import { isTransientNetworkError } from "./utils/clientEnvironment";



// 1. SCAN ALL PAGES AUTOMATICALLY (lazy to reduce initial mobile startup cost)
const pages = (import.meta as any).glob(["./pages/**/*.tsx", "!./pages/**/*.test.tsx", "!./pages/**/*.spec.tsx"]);

const CHUNK_RELOAD_FLAG = "__collectohub_chunk_reload_once__";

if (typeof window !== "undefined") {
    window.addEventListener("load", () => {
        sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    });
}

const isChunkLoadError = (error: unknown): boolean => {
    if (!error) return false;
    const message = String((error as any)?.message || error);
    return /loading chunk|failed to fetch dynamically imported module|importing a module script failed|cannot read properties of undefined \(reading ['"]default['"]\)/i.test(message);
};

class RouteChunkErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: unknown) {
        if (!isTransientNetworkError(error)) {
            console.error("Route chunk render error:", error);
        }
        if (isChunkLoadError(error) && typeof window !== "undefined") {
            const reloaded = sessionStorage.getItem(CHUNK_RELOAD_FLAG);
            if (!reloaded) {
                sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1");
                window.location.reload();
            }
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <Box p={3} sx={{ display: "grid", gap: 1.5, textAlign: "center", maxWidth: 520, mx: "auto" }}>
                    <Typography variant="h6">Bağlantı Sorunu</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Yeni sürüm yüklenirken geçici bir bağlantı problemi oluştu.
                    </Typography>
                    <Box>
                        <button onClick={() => window.location.reload()}>Sayfayı yenile</button>
                    </Box>
                </Box>
            );
        }
        return this.props.children;
    }
}

const RouteSkeleton = () => {
    const theme = useTheme();
    const cardBg = alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.5 : 0.85);

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto" }}>
            <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center" }}>
                <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="min(62%, 360px)" height={44} />
                    <Skeleton variant="text" width="min(86%, 520px)" height={22} />
                </Box>
                <Skeleton variant="rounded" width={120} height={38} sx={{ borderRadius: 2, display: { xs: "none", sm: "block" } }} />
            </Box>
            <Box sx={{ p: 2, borderRadius: 4, bgcolor: cardBg, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
                <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3, mb: 2 }} />
                {[0, 1, 2].map((item) => (
                    <Box key={item} sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
                        <Skeleton variant="text" width={item === 0 ? "35%" : "25%"} height={22} />
                        <Skeleton variant="text" width={item === 1 ? "42%" : "30%"} height={22} />
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

const LazyLayout = React.lazy(() =>
    import("./components/layout/CustomLayout").then((module) => ({ default: module.CustomLayout }))
);
const LazyCookieConsentBanner = React.lazy(() => import("./components/CookieConsentBanner"));
const LazyComplianceGateModal = React.lazy(() =>
    import("./components/legal/ComplianceGateModal").then((module) => ({ default: module.ComplianceGateModal }))
);

const Layout: React.FC<React.PropsWithChildren> = ({ children }) => (
    <React.Suspense fallback={<RouteSkeleton />}>
        <LazyLayout>{children}</LazyLayout>
    </React.Suspense>
);

// 2. CREATE A DYNAMIC COMPONENT MAP
const dynamicComponents: Record<string, React.FC> = {};
const initialResources: ResourceProps[] = [];

Object.keys(pages).forEach((path) => {
    const normalizedPath = path
        .replace("./pages", "")
        .replace(".tsx", "")
        .replace("/index", "") // Handle /models/index -> /models
        .replace(/\\/g, "/")
        .replace(/\/+/g, "/")
        .toLowerCase();

    const finalPath = normalizedPath === "" ? "/" : normalizedPath;
    const modLoader = pages[path] as (() => Promise<any>);
    const LazyPageComponent = React.lazy(async () => {
        const mod = await modLoader();
        const PageComponent = mod?.default && typeof mod.default === "function"
            ? mod.default
            : Object.keys(mod || {}).map((k) => mod[k]).find((v) => typeof v === "function");

        if (!PageComponent) {
            return { default: ErrorPage };
        }

        return { default: PageComponent };
    });

    const WrappedComponent = (props: any) => (
        <React.Suspense fallback={<RouteSkeleton />}>
            <RouteChunkErrorBoundary>
                <LazyPageComponent {...props} />
            </RouteChunkErrorBoundary>
        </React.Suspense>
    );

    dynamicComponents[finalPath] = WrappedComponent;

    // Add to initial resources (only index pages are primary resources)
    if (path.endsWith("index.tsx") || path.includes("/legal/")) {
        const publicPaths = ["/auth", "/error", "/login", "/register", "/forgot-password", "/updatepassword", "/confirmemail", "/legal/document", "/legal/content-notice", "/legal/eula", "/legal/privacy", "/legal/competition-rules"];
        const checkPath = finalPath === "" ? "/" : finalPath;
        if (!publicPaths.includes(checkPath)) {
            const name = checkPath.substring(1).replace(/\//g, "-") || "home";
            let label = name.charAt(0).toUpperCase() + name.slice(1);
            let parent = undefined;

            if (name.startsWith("admin-")) {
                label = name.replace("admin-", "").replace(/-/g, " ");
                label = label.charAt(0).toUpperCase() + label.slice(1);
                
                // Specific overrides
                if (name === "admin-abuse-reports") label = "Åikayet YÃ¶netimi";
                if (name === "admin-feedbacks") label = "Geri Bildirimler";
                if (name === "admin-users") label = "KullanÄ±cÄ±lar";
                if (name === "admin-brands") label = "Markalar";
                if (name === "admin-fields") label = "Alan TanÄ±mlarÄ±";
                if (name === "admin-models") label = "Modeller";
                if (name === "admin-collections") label = "Koleksiyonlar";
                if (name === "admin-badges") label = "Rozetler";
                if (name === "admin-media-approvals") label = "GÃ¶rsel OnaylarÄ±";
                if (name === "admin-market") label = "Market YÃ¶netimi";
                if (name === "admin-cosmetic-editor") label = "Kozmetik TasarÄ±mcÄ±";
                if (name === "admin-ad-settings") label = "Reklam YÃ¶netimi";
                if (name === "admin-economy") label = "Ekonomi YÃ¶netimi";
                if (name === "admin-mystery-box") label = "Gizemli Kutu";
                if (name === "admin-daily-login") label = "GiriÅŸ Takvimi";
                if (name === "admin-quest-templates") label = "GÃ¶rev YÃ¶netimi";
                if (name === "admin-game-levels") label = "Seviye YÃ¶netimi";
                if (name === "admin-analytics") label = "Analitik";
                if (name === "admin-email-verifications") label = "Onay Mailleri";
                if (name === "admin-visual-search-audits") label = "Görsel Arama Denetimi";
                if (name === "admin-policies") label = "Hukuki Metinler";
                
                parent = "YÃ¶netim Paneli";
            }

            if (name === "system-maintenance-monitor") {
                label = "BakÄ±m MonitÃ¶rÃ¼";
                parent = "Sistem";
            }

            if (name === "system-maintenance-audit") {
                label = "GÃ¶rsel Denetimi";
                parent = "Sistem";
            }

            if (name === "system-maintenance-media-approvals") {
                label = "GÃ¶rsel OnaylarÄ±";
                parent = "Sistem";
            }

            if (name === "system-security-abuse") {
                label = "Abuse Monitor";
                parent = "Sistem";
            }

            if (name === "wiki") label = "Koleksiyon Wiki";
            if (name === "community") label = "Topluluk";
            if (name === "listings") label = "Takas & SatÄ±ÅŸ";

            // Check if edit.tsx or create.tsx or show.tsx exists in the same folder
            const editPath = path.replace("index.tsx", "edit.tsx");
            const createPath = path.replace("index.tsx", "create.tsx");
            const showPath = path.replace("index.tsx", "show.tsx");

            const hasEdit = !!pages[editPath];
            const hasCreate = !!pages[createPath];
            const hasShow = !!pages[showPath];

            const isProfile = name === "profile";
            const resource: ResourceProps = {
                name: name,
                list: checkPath,
                edit: hasEdit ? checkPath + (isProfile ? "/edit" : "/edit/:id") : undefined,
                create: hasCreate ? checkPath + "/create" : undefined,
                show: hasShow ? checkPath + "/show/:id" : undefined,
                meta: {
                    component: WrappedComponent,
                    label: label,
                    parent: parent,
                    icon: name === "listings" ? <StorefrontOutlinedIcon sx={{ fontSize: '1.2rem' }} /> : undefined
                }
            };
            initialResources.push(resource);
        }
    }
});


const MobileBridgeTracker = () => {
    const { data: identity, isLoading } = useGetIdentity<any>();
    const isAppReadySent = React.useRef(false);

    useEffect(() => {
        if (isLoading) return;
        if (isAppReadySent.current) return;

        if (typeof window !== "undefined") {
            const handler = (window as any).flutter_inappwebview?.callHandler;
            if (handler) {
                isAppReadySent.current = true;
                handler("APP_READY", {
                    version: 2,
                    event: "APP_READY",
                    type: "LOG",
                    requestId: `app_ready_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                    timestamp: new Date().toISOString(),
                    payload: {
                        route: window.location.pathname + window.location.search,
                        authState: identity?.id ? "authenticated" : "anonymous",
                        hasToken: !!localStorage.getItem(TOKEN_KEY),
                        appReadyMs: Math.round(performance.now()),
                        authResolveMs: (window as any).__collectoHubAuthResolveMs || null,
                        userAgent: navigator.userAgent,
                        screenWidth: window.innerWidth,
                        screenHeight: window.innerHeight
                    }
                }).catch(() => {});
            }
        }
    }, [identity, isLoading]);

    return null;
};

const RedirectToSearch = () => {
    const location = useLocation();
    return <Navigate to={{ pathname: "/search", search: location.search }} replace />;
};

const safeInternalPath = (to?: string | null) => {
    if (!to) return "/dashboard";
    if (!to.startsWith("/")) return "/dashboard";
    if (to.startsWith("//")) return "/dashboard";
    if (to.toLowerCase().startsWith("javascript:")) return "/dashboard";
    return to;
};

const AuthRedirect = () => {
    const [searchParams] = useSearchParams();
    const to = searchParams.get("to");
    const safePath = safeInternalPath(to);
    return <Navigate to={safePath} replace />;
};

const LegalDocumentRoute = () => {
    const location = useLocation();
    if (location.pathname === "/legal/content-notice") return <ContentNoticePage />;
    const Component = dynamicComponents["/legal/document"];
    return Component ? <Component key={location.pathname} /> : <ErrorPage />;
};

const FRONTEND_NAV_LOG_DEDUPE_KEY = "ch:last-nav-log";

const FrontendNavigationTracker = () => {
    const location = useLocation();
    const previousPathRef = React.useRef<string>("");
    const isFirstNavigationRef = React.useRef(true);
    const lastPathnameRef = React.useRef<string>("");

    useEffect(() => {
        captureFirstTouch();
        const currentPath = telemetryPath(location.pathname);
        if (!currentPath || currentPath.startsWith("/api")) {
            previousPathRef.current = currentPath;
            return;
        }

        if (location.pathname !== lastPathnameRef.current) {
            lastPathnameRef.current = location.pathname;

            const getPageType = (path: string) => {
                if (path === "/" || path === "/landing") return "landing";
                if (path.startsWith("/search/show/") || path.startsWith("/models/show/")) return "model_detail";
                if (path === "/search") return "search";
                if (path.startsWith("/brands/")) return "brand_detail";
                if (path.startsWith("/collection/")) return "user_collection";
                return "other";
            };

            trackEvent(ANALYTICS_EVENTS.VISITOR_LANDED, {
                page_path: telemetryPath(location.pathname),
                page_type: getPageType(location.pathname),
                referrer: telemetryReferrer(document.referrer) || undefined
            });
        }

        let cancelled = false;
        const navigationType = isFirstNavigationRef.current ? "PAGE_LOAD" : "SPA_NAVIGATION";
        const startedAt = performance.now();

        const sendLog = async (durationMs: number) => {
            if (!getConsent().analytics) return;
            const dedupePayload = `${navigationType}:${currentPath}`;
            const previousRaw = sessionStorage.getItem(FRONTEND_NAV_LOG_DEDUPE_KEY);
            if (previousRaw) {
                try {
                    const previous = JSON.parse(previousRaw) as { key?: string; timestamp?: number };
                    if (previous.key === dedupePayload && previous.timestamp && Date.now() - previous.timestamp < 1500) {
                        return;
                    }
                } catch {
                    // no-op
                }
            }

            sessionStorage.setItem(
                FRONTEND_NAV_LOG_DEDUPE_KEY,
                JSON.stringify({ key: dedupePayload, timestamp: Date.now() })
            );

            const token = localStorage.getItem(TOKEN_KEY);
            const navigationEntry = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
            const scriptResources = performance
                .getEntriesByType("resource")
                .filter((entry) => (entry as PerformanceResourceTiming).initiatorType === "script") as PerformanceResourceTiming[];
            const scriptTransferKb = Math.round(
                scriptResources.reduce((sum, entry) => sum + ((entry.transferSize || 0) / 1024), 0)
            );
            const scriptDecodedKb = Math.round(
                scriptResources.reduce((sum, entry) => sum + ((entry.decodedBodySize || 0) / 1024), 0)
            );
            const authContextResolveMsRaw = sessionStorage.getItem("ch_my_collection_auth_resolve_ms");
            const pageMountMsRaw = sessionStorage.getItem("ch_my_collection_page_mount_ms");

            const ttfbMs = navigationEntry ? Math.max(0, Math.round(navigationEntry.responseStart - navigationEntry.requestStart)) : null;
            const appReadyMs = (window as any).__collectoHubAppReadyMs || null;
            const authResolveMs = (window as any).__collectoHubAuthResolveMs || null;

            let platform = "Web";
            if (typeof window !== "undefined") {
                const ua = window.navigator.userAgent.toLowerCase();
                if (ua.includes("iphone") || ua.includes("ipad")) platform = "iOS";
                else if (ua.includes("android")) platform = "Android";
            }

            try {
                await fetch(`${APP_CONFIG.API_URL}/api/Performance/frontend-route`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...(token ? { Authorization: `Bearer ${token}` } : {})
                    },
                    body: JSON.stringify({
                        path: currentPath,
                        previousPath: previousPathRef.current || null,
                        title: document.title,
                        durationMs,
                        navigationType,
                        domContentLoadedMs: navigationEntry ? Math.round(navigationEntry.domContentLoadedEventEnd) : null,
                        loadEventMs: navigationEntry ? Math.round(navigationEntry.loadEventEnd) : null,
                        scriptResourceCount: scriptResources.length,
                        scriptTransferKb,
                        scriptDecodedKb,
                        navigationTransferKb: navigationEntry ? Math.round((navigationEntry.transferSize || 0) / 1024) : null,
                        navigationDecodedKb: navigationEntry ? Math.round((navigationEntry.decodedBodySize || 0) / 1024) : null,
                        authContextResolveMs: authResolveMs || (authContextResolveMsRaw ? parseInt(authContextResolveMsRaw, 10) : null),
                        pageMountMs: pageMountMsRaw ? parseInt(pageMountMsRaw, 10) : null,
                        ttfbMs,
                        appReadyMs,
                        platform,
                        appVersion: "1.0.0",
                        extraJson: null
                    }),
                    keepalive: true
                });
            } catch {
                // no-op
            }
        };

        let wasHidden = document.visibilityState === "hidden";
        const handleVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                wasHidden = true;
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);

        if (isFirstNavigationRef.current) {
            isFirstNavigationRef.current = false;
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            if (wasHidden) {
                previousPathRef.current = currentPath;
                return;
            }
            const entry = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
            const initialDuration = Math.round(entry?.domContentLoadedEventEnd || entry?.duration || performance.now());
            void sendLog(Math.max(initialDuration, 0));
            previousPathRef.current = currentPath;
            return;
        }

        // Android WebView can throttle requestAnimationFrame to roughly one
        // second in background/transition states. A macrotask measures the
        // committed SPA navigation without turning that throttle into fake
        // ~950 ms route durations.
        const measurementTimer = window.setTimeout(() => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            if (cancelled || wasHidden || document.visibilityState === "hidden") return;
            const durationMs = Math.round(performance.now() - startedAt);
            void sendLog(Math.max(durationMs, 0));
            previousPathRef.current = currentPath;
        }, 0);

        return () => {
            cancelled = true;
            window.clearTimeout(measurementTimer);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [location.pathname, location.search]);

    return null;
};

const callMobileBridge = (message: string, type: "LOG" | "WARN" | "ERR" = "LOG", payload?: unknown) => {
    try {
        const bridge = (window as any).flutter_inappwebview;
        if (!bridge?.callHandler) return;
        bridge.callHandler("CollectoMobile", {
            version: 2,
            event: message,
            type,
            requestId: `${message.toLowerCase()}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            timestamp: new Date().toISOString(),
            payload: payload ?? {}
        });
    } catch {
        // no-op: browser/web execution
    }
};

const MobileBridgeNotifier: React.FC<{ token: string | null }> = ({ token }) => {
    const location = useLocation();

    useEffect(() => {
        let cancelled = false;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (cancelled) return;
                callMobileBridge("APP_READY", "LOG", {
                    path: telemetryPath(location.pathname),
                    title: document.title,
                    readyState: document.readyState,
                    hasToken: !!localStorage.getItem(TOKEN_KEY),
                    timestamp: Date.now(),
                });
            });
        });
        return () => {
            cancelled = true;
        };
    }, [location.pathname, location.search]);

    useEffect(() => {
        const currentToken = localStorage.getItem(TOKEN_KEY);
        if (!currentToken) return;
        callMobileBridge("TOKEN_UPDATED", "LOG", {
            token: currentToken,
            path: telemetryPath(location.pathname),
            timestamp: Date.now(),
        });
    }, [token, location.pathname, location.search]);

    return null;
};

const ConsentSyncTrigger: React.FC = () => {
    useConsentSync();
    return null;
};

const RefineTitle: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
    const theme = useTheme();

    return (
        <Box sx={{
            height: "64px",
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-start",
            padding: collapsed ? "0" : "0 24px",
            gap: "12px",
            cursor: "pointer",
            transition: "all 0.3s ease",
            borderBottom: `1px solid ${theme.palette.divider}`
        }}>
            <Box
                component="img"
                src={collapsed ? "/favicon-32x32.png" : (theme.palette.mode === 'dark' ? "/logo-light.png" : "/logo-dark.png")}
                alt="CollectoHub"
                sx={{
                    height: collapsed ? "32px" : "32px",
                    width: "auto",
                    transition: "all 0.3s ease",
                    transform: collapsed ? "scale(1.1)" : "scale(1)"
                }}
            />
            {!collapsed && (
                <Typography
                    variant="h6"
                    noWrap
                    sx={{
                        fontWeight: 800,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        letterSpacing: "-0.5px",
                        fontSize: "1.25rem"
                    }}
                >
                    CollectoHub
                </Typography>
            )}
        </Box>
    );
};

const RefineAppContent: React.FC<{ token: string | null; resources: ResourceProps[]; Landing: any }> = ({ token, resources, Landing }) => {
    const toast = useToast();
    const navigate = useNavigate();

    useEffect(() => {
        const handler = (event: Event) => {
            const customEvent = event as CustomEvent<{
                path?: string;
                payload?: {
                    path?: string;
                    targetUrl?: string;
                    source?: string;
                    deepLinkId?: string;
                };
                version?: number;
                event?: string;
                requestId?: string;
            }>;

            const detail = customEvent.detail;
            const path = detail?.payload?.path ?? detail?.path;

            if (!path || !path.startsWith("/")) {
                console.warn("[CollectoBridge] Invalid mobile navigation payload", detail);
                return;
            }

            navigate(path);
        };

        (window as any).__COLLECTOHUB_MOBILE_NAV_LISTENER_REGISTERED__ = true;
        window.addEventListener("COLLECTOHUB_MOBILE_NAVIGATE", handler);

        return () => {
            (window as any).__COLLECTOHUB_MOBILE_NAV_LISTENER_REGISTERED__ = false;
            window.removeEventListener("COLLECTOHUB_MOBILE_NAVIGATE", handler);
        };
    }, [navigate]);

    const customNotificationProvider = React.useMemo(() => ({
        open: ({ message, type }: any) => {
            if (type === "success") {
                toast.success(message);
            } else if (type === "error") {
                toast.error(message);
            } else {
                toast.info(message);
            }
        },
        close: () => {}
    }), [toast]);

    return (
        <Refine
            key={token || "unauthenticated"}
            routerProvider={routerBindings}
            dataProvider={dataProvider}
            authProvider={authProvider}
            resources={resources}
            notificationProvider={customNotificationProvider}
            Title={RefineTitle}
            options={{
                syncWithLocation: true,
                warnWhenUnsavedChanges: true,
                useNewQueryKeys: true,
                projectId: "iYLRgK-J8UR6r-1ihygH",
                disableTelemetry: true,
            }}
        >
        <SignalRProvider>
            <MobileBridgeTracker />
            <Routes>
                {/* Legacy Redirects */}
                <Route path="/privacy-policy" element={<Navigate to="/legal/privacy" replace />} />
                <Route path="/eula" element={<Navigate to="/legal/terms" replace />} />
                <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
                <Route path="/competition-rules" element={<Navigate to="/legal/competition-rules" replace />} />

                {/* Public Auth Routes */}
                <Route path="/confirm-email" element={dynamicComponents["/confirmemail"] ? React.createElement(dynamicComponents["/confirmemail"]) : <ErrorPage />} />

                <Route
                    element={
                        <Authenticated key="auth-pages" fallback={<Outlet />}>
                            <AuthRedirect />
                        </Authenticated>
                    }
                >
                    <Route path="/login" element={dynamicComponents["/login"] ? React.createElement(dynamicComponents["/login"]) : <ErrorPage />} />
                    <Route path="/register" element={dynamicComponents["/register"] ? React.createElement(dynamicComponents["/register"]) : <ErrorPage />} />
                    <Route path="/forgot-password" element={dynamicComponents["/forgotpassword"] ? React.createElement(dynamicComponents["/forgotpassword"]) : <ErrorPage />} />
                    <Route path="/update-password" element={dynamicComponents["/updatepassword"] ? React.createElement(dynamicComponents["/updatepassword"]) : <ErrorPage />} />
                </Route>

                {/* Public Pages with Layout */}
                <Route
                    element={
                        <Layout>
                            <Outlet />
                        </Layout>
                    }
                >
                    {/* Root & Landing inside Layout */}
                    <Route
                        path="/"
                        element={token ? <Navigate to="/dashboard" replace /> : (Landing ? <Landing /> : <Navigate to="/login" />)}
                    />
                    <Route 
                        path="/landing" 
                        element={token ? <Navigate to="/dashboard" replace /> : (Landing ? <Landing /> : <Navigate to="/login" />)} 
                    />

                    <Route path="/models" element={<RedirectToSearch />} />
                    <Route path="/models/show" element={<RedirectToSearch />} />
                    <Route path="/search" element={dynamicComponents["/search"] ? React.createElement(dynamicComponents["/search"]) : <ErrorPage />} />
                    {["/wiki", "/wiki/:brandSlug", "/wiki/:brandSlug/:slug"].map(path => <Route key={path} path={path} element={dynamicComponents["/wiki"] ? React.createElement(dynamicComponents["/wiki"]) : <ErrorPage />} />)}
                    <Route path="/brands" element={dynamicComponents["/brands"] ? React.createElement(dynamicComponents["/brands"]) : <ErrorPage />} />
                    <Route path="/community" element={dynamicComponents["/community"] ? React.createElement(dynamicComponents["/community"]) : <ErrorPage />} />
                    <Route path="/collectors" element={dynamicComponents["/collectors"] ? React.createElement(dynamicComponents["/collectors"]) : <ErrorPage />} />
                    <Route path="/listings" element={dynamicComponents["/listings"] ? React.createElement(dynamicComponents["/listings"]) : <ErrorPage />} />
                    <Route path="/listings/show/:id" element={dynamicComponents["/listings/show"] ? React.createElement(dynamicComponents["/listings/show"]) : <ErrorPage />} />
                    <Route path="/stores/:slug/products/:offerId" element={dynamicComponents["/stores/product"] ? React.createElement(dynamicComponents["/stores/product"]) : <ErrorPage />} />
                    <Route path="/stores/:slug" element={dynamicComponents["/stores/show"] ? React.createElement(dynamicComponents["/stores/show"]) : <ErrorPage />} />
                    <Route path="/legal/competition-rules" element={dynamicComponents["/legal/competition-rules"] ? React.createElement(dynamicComponents["/legal/competition-rules"]) : <ErrorPage />} />
                    <Route path="/legal/:slug" element={<LegalDocumentRoute />} />
                    
                    {/* Public Detail Routes */}
                    <Route path="/community/post/:slug" element={dynamicComponents["/community/post"] ? React.createElement(dynamicComponents["/community/post"]) : <ErrorPage />} />
                    <Route 
                        path="/profile/inventory" 
                        element={
                            <Authenticated key="profile-inventory" fallback={<CatchAllNavigate to="/login" />}>
                                {dynamicComponents["/profile/inventory"] ? React.createElement(dynamicComponents["/profile/inventory"]) : <ErrorPage />}
                            </Authenticated>
                        } 
                    />
                    <Route 
                        path="/profile/creator" 
                        element={
                            <Authenticated key="profile-creator" fallback={<CatchAllNavigate to="/login" />}>
                                {dynamicComponents["/profile/creator"] ? React.createElement(dynamicComponents["/profile/creator"]) : <ErrorPage />}
                            </Authenticated>
                        } 
                    />
                    <Route path="/profile/:username" element={dynamicComponents["/profile"] ? React.createElement(dynamicComponents["/profile"]) : <ErrorPage />} />
                    <Route path="/collection/:username" element={dynamicComponents["/profile"] ? React.createElement(dynamicComponents["/profile"]) : <ErrorPage />} />
                    <Route path="/garage/:username" element={dynamicComponents["/profile"] ? React.createElement(dynamicComponents["/profile"]) : <ErrorPage />} />
                    <Route path="/garage/:username/collections/:collectionSlug" element={dynamicComponents["/public-garage/collection"] ? React.createElement(dynamicComponents["/public-garage/collection"]) : <ErrorPage />} />
                    <Route path="/brand/:slug" element={dynamicComponents["/brand"] ? React.createElement(dynamicComponents["/brand"]) : <ErrorPage />} />
                    <Route path="/models/show/:slug" element={dynamicComponents["/search/show"] ? React.createElement(dynamicComponents["/search/show"]) : <ErrorPage />} />
                    <Route path="/search/show" element={<RedirectToSearch />} />
                    <Route path="/search/show/:slug" element={dynamicComponents["/search/show"] ? React.createElement(dynamicComponents["/search/show"]) : <ErrorPage />} />
                    <Route path="/my-collection/collections/:collectionSlug" element={dynamicComponents["/my-collection"] ? React.createElement(dynamicComponents["/my-collection"]) : <ErrorPage />} />
                    <Route path="/my-collection/toy/:id" element={dynamicComponents["/my-collection/toy"] ? React.createElement(dynamicComponents["/my-collection/toy"]) : <ErrorPage />} />
                </Route>

                {/* Private Routes with Layout */}
                <Route
                    element={
                        <Authenticated key="authenticated-routes" fallback={<CatchAllNavigate to="/login" />}>
                            <Layout>
                                <Outlet />
                            </Layout>
                        </Authenticated>
                    }
                >
                    <Route path="/dashboard" element={dynamicComponents["/dashboard"] ? React.createElement(dynamicComponents["/dashboard"]) : <ErrorPage />} />
                    <Route path="/onboarding/account-type" element={dynamicComponents["/onboarding/account-type"] ? React.createElement(dynamicComponents["/onboarding/account-type"]) : <ErrorPage />} />
                    <Route path="/seller/apply" element={dynamicComponents["/seller/apply"] ? React.createElement(dynamicComponents["/seller/apply"]) : <ErrorPage />} />
                    <Route path="/games/model-match" element={dynamicComponents["/games/model-match"] ? React.createElement(dynamicComponents["/games/model-match"]) : <ErrorPage />} />
                    <Route path="/games/model-match/classic" element={dynamicComponents["/games/model-match/classic"] ? React.createElement(dynamicComponents["/games/model-match/classic"]) : <ErrorPage />} />
                    <Route path="/games/model-match/mahjong" element={dynamicComponents["/games/model-match/mahjong"] ? React.createElement(dynamicComponents["/games/model-match/mahjong"]) : <ErrorPage />} />
                    <Route path="/market" element={dynamicComponents["/market"] ? React.createElement(dynamicComponents["/market"]) : <ErrorPage />} />
                    <Route path="/profile" element={dynamicComponents["/profile"] ? React.createElement(dynamicComponents["/profile"]) : <ErrorPage />} />
                    <Route path="/profile/edit" element={dynamicComponents["/profile/edit"] ? React.createElement(dynamicComponents["/profile/edit"]) : <ErrorPage />} />
                    <Route path="/notifications" element={dynamicComponents["/notifications"] ? React.createElement(dynamicComponents["/notifications"]) : <ErrorPage />} />
                    <Route path="/messages" element={dynamicComponents["/messages"] ? React.createElement(dynamicComponents["/messages"]) : <ErrorPage />} />
                    <Route path="/messages/:id" element={dynamicComponents["/messages/list"] ? React.createElement(dynamicComponents["/messages/list"]) : <ErrorPage />} />
                    <Route path="/community/create" element={dynamicComponents["/community/create"] ? React.createElement(dynamicComponents["/community/create"]) : <ErrorPage />} />
                    <Route path="/community/edit/:id" element={dynamicComponents["/community/edit"] ? React.createElement(dynamicComponents["/community/edit"]) : <ErrorPage />} />
                    <Route path="/admin/ad-settings" element={dynamicComponents["/admin/ad-settings"] ? React.createElement(dynamicComponents["/admin/ad-settings"]) : <ErrorPage />} />
                    <Route path="/admin/analytics" element={dynamicComponents["/admin/analytics"] ? React.createElement(dynamicComponents["/admin/analytics"]) : <ErrorPage />} />
                    <Route path="/listings/create" element={dynamicComponents["/listings/create"] ? React.createElement(dynamicComponents["/listings/create"]) : <ErrorPage />} />
                    <Route path="/listings/edit/:id" element={dynamicComponents["/listings/edit"] ? React.createElement(dynamicComponents["/listings/edit"]) : <ErrorPage />} />
                    <Route path="/listings/saved" element={dynamicComponents["/listings/saved"] ? React.createElement(dynamicComponents["/listings/saved"]) : <ErrorPage />} />
                    {resources.map((resource) => {
                        const listPath = typeof resource.list === "string" ? resource.list : undefined;
                        if (!listPath) return null;

                        const routePath = listPath.startsWith("/") ? listPath.substring(1) : listPath;

                        const searchPath = listPath.startsWith("/") ? listPath.toLowerCase() : "/" + listPath.toLowerCase();
                        const Component = dynamicComponents[searchPath];
                        if (!Component) return null;

                        // Skip public/legal routes that are already statically defined
                        const exactPublicPaths = ["", "landing", "models", "search", "wiki", "community", "collectors", "login", "register", "forgot-password", "listings"];
                        if (exactPublicPaths.includes(routePath) || routePath.startsWith("legal/")) return null;

                        const subRoutes = [];
                        
                        // 1. List route
                        subRoutes.push(
                            <Route key={`${resource.name}-list`} path={routePath} element={<Component />} />
                        );

                        // 2. Edit route
                        if (typeof resource.edit === "string") {
                            const editPath = resource.edit.startsWith("/") ? resource.edit.substring(1) : resource.edit;
                            subRoutes.push(
                                <Route key={`${resource.name}-edit`} path={editPath} element={React.createElement(dynamicComponents[(searchPath + "/edit").toLowerCase()] || Component)} />
                            );
                        }

                        // 3. Create route
                        if (typeof resource.create === "string") {
                            const createPath = resource.create.startsWith("/") ? resource.create.substring(1) : resource.create;
                            subRoutes.push(
                                <Route key={`${resource.name}-create`} path={createPath} element={React.createElement(dynamicComponents[(searchPath + "/create").toLowerCase()] || Component)} />
                            );
                        }

                        // 4. Show route
                        if (typeof resource.show === "string") {
                            const showPath = resource.show.startsWith("/") ? resource.show.substring(1) : resource.show;
                            subRoutes.push(
                                <Route key={`${resource.name}-show`} path={showPath} element={React.createElement(dynamicComponents[(searchPath + "/show").toLowerCase()] || Component)} />
                            );
                        }

                        return (
                            <React.Fragment key={resource.name}>
                                {subRoutes}
                            </React.Fragment>
                        );
                    })}
                </Route>

                <Route path="*" element={<ErrorPage />} />
            </Routes>
        </SignalRProvider>

            <UnsavedChangesNotifier />
            <PrivateRouteSeo />
        </Refine>
    );
};

const PrivateRouteSeo = () => {
    const { pathname } = useLocation();
    const privateRoute = /^\/(?:login|register|forgot-password|update-password|confirm-email|dashboard|admin|system|my-collection|notifications|messages)(?:\/|$)/.test(pathname)
        || /^\/profile(?:\/(?:edit|inventory|creator))?$/.test(pathname)
        || /^\/(?:listings|community)\/(?:create|edit|saved)(?:\/|$)/.test(pathname);
    if (!privateRoute) return null;
    return <>
        <Helmet><meta name="robots" content="noindex,follow" /></Helmet>
        <DocumentTitleHandler handler={(item) => {
            const label = item.resource?.meta?.label || item.resource?.name;
            return label ? `${label} | CollectoHub` : "CollectoHub";
        }} />
    </>;
};

const App: React.FC = () => {
    useNotifications();

    useEffect(() => {
        if (typeof window !== "undefined") {
            (window as any).__collectoHubAppReadyMs = Math.round(performance.now());
        }
    }, []);

    const [resources, setResources] = useState<ResourceProps[]>(() => {
        try {
            const cached = localStorage.getItem("ch_cached_resources");
            if (cached) {
                const parsed = JSON.parse(cached);
                // Map components back since JSON doesn't store functions
                return parsed.map((r: any) => ({
                    ...r,
                    meta: {
                        ...r.meta,
                        component: r.meta?.componentName ? dynamicComponents[r.meta.componentName] : undefined
                    }
                }));
            }
        } catch {
            // no-op
        }
        return initialResources;
    });
    const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
    const lastLoadedTokenRef = React.useRef<string | null | undefined>(undefined);
    const inFlightRef = React.useRef(false);

    const fetchResources = useCallback(async (force = false) => {
        try {
            const currentToken = localStorage.getItem(TOKEN_KEY);

            // Prevent duplicate calls in StrictMode/dev double-invocation.
            if (!force && inFlightRef.current) return;
            if (!force && lastLoadedTokenRef.current === currentToken) return;

            if (currentToken) {
                inFlightRef.current = true;
                const response = await fetch(`${APP_CONFIG.API_URL}/api/AppMenu`, {
                    headers: { "Authorization": `Bearer ${currentToken}` }
                });
                
                if (!response.ok) throw new Error("Menu sync failed");

                const syncedData = await response.json();
                
                if (Array.isArray(syncedData)) {
                    // Map API DTOs to CollectoHub ResourceProps with strict null safety
                    const mappedSyncedResources: ResourceProps[] = syncedData
                        .filter(item => item && (item.name || item.Name)) // Ensure item and name exist
                        .map((item, index) => {
                            const rawName = item.name || item.Name;
                            const rawList = item.list || item.List;
                            const meta = item.meta || item.Meta || {};

                            return {
                                name: rawName,
                                list: typeof rawList === "string" ? rawList : undefined,
                                meta: {
                                    ...meta,
                                    orderNo: item.orderNo ?? item.OrderNo,
                                    menuOrderIndex: index,
                                    menuSource: "api",
                                    componentName: rawList?.toLowerCase() || ""
                                }
                            };
                        });

                    // Match dynamic API resources with local folder metadata to inherit edit/create/show properties
                    const finalResources = mappedSyncedResources.map(synced => {
                        const normalizedSyncedName = synced.name.replace(/\//g, "-");
                        const matchingLocal = initialResources.find(fr => 
                            fr.name === synced.name || 
                            fr.name === normalizedSyncedName
                        );
                        
                        if (matchingLocal) {
                            return {
                                ...synced,
                                name: matchingLocal.name, // Align with frontend-friendly hyphenated name
                                edit: matchingLocal.edit,
                                create: matchingLocal.create,
                                show: matchingLocal.show,
                                meta: {
                                    ...matchingLocal.meta,
                                    ...synced.meta
                                }
                            };
                        }
                        return synced;
                    });

                    // Keep public/whitelisted local resources that are NOT in the API
                    initialResources.forEach((fr) => {
                    const publicWhitelisted = [
                        "models",
                        "search",
                        "brands",
                        "wiki",
                        "brand",
                        "community",
                        "collectors",
                        "home",
                        "dashboard",
                        "market",
                        "notifications",
                        "messages",
                        "profile",
                        "my-collection",
                        "wishlist",
                        "friends",
                        "garage",
                        "listings",
                        "admin-analytics",
                        "admin-quest-templates"
                        ];
                        if (fr && fr.name && publicWhitelisted.includes(fr.name)) {
                            if (!finalResources.find(r => r.name === fr.name)) {
                                finalResources.push(fr);
                            }
                        }
                    });

                    const filteredResources = finalResources.filter(r => r && r.name);
                    setResources(filteredResources);
                    try {
                        localStorage.setItem("ch_cached_resources", JSON.stringify(filteredResources));
                    } catch {
                        // no-op
                    }
                    lastLoadedTokenRef.current = currentToken;
                }
            } else {
                setResources(initialResources);
                try {
                    localStorage.removeItem("ch_cached_resources");
                } catch {
                    // no-op
                }
                lastLoadedTokenRef.current = currentToken;
            }
        } catch (error) {
            console.error("Failed to sync resources:", error);
        } finally {
            inFlightRef.current = false;
        }
    }, []);

    useEffect(() => {
        fetchResources();

        const handleAuthStateChange = () => {
            setToken(localStorage.getItem(TOKEN_KEY));
            fetchResources(true);
        };

        window.addEventListener("auth-state-changed", handleAuthStateChange);
        return () => {
            window.removeEventListener("auth-state-changed", handleAuthStateChange);
        };
    }, [fetchResources]);

    const Landing = dynamicComponents["/landing"];

    return (
        <HelmetProvider>
            <BrowserRouter>
                <FrontendNavigationTracker />
                <MobileBridgeNotifier token={token} />
                <ToastProvider>
                    <TourProvider>
                        <LanguageProvider>
                            <ColorModeContextProvider>
                                <CssBaseline />

                                <GlobalStyles styles={(theme) => ({
                                    html: {
                                        WebkitFontSmoothing: "auto",
                                        height: "100%",
                                        backgroundColor: theme.palette.background.default,
                                        "--safe-area-inset-top": "env(safe-area-inset-top, 0px)",
                                        "--safe-area-inset-bottom": "env(safe-area-inset-bottom, 0px)",
                                        overscrollBehaviorY: "none",
                                    },
                                    "#root": {
                                        minHeight: "100%",
                                        backgroundColor: theme.palette.background.default,
                                    },
                                    "#react-joyride-portal": {
                                        zIndex: "5000 !important",
                                        position: "relative",
                                    },
                                    "body": {
                                        top: "0px !important",
                                        position: "static !important",
                                        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important",
                                        minHeight: "100vh",
                                        margin: 0,
                                        backgroundColor: theme.palette.background.default,
                                        overscrollBehaviorY: "none",
                                    },
                                    "@media (max-width: 900px)": {
                                        "html, body, *": {
                                            scrollbarWidth: "none",
                                            msOverflowStyle: "none",
                                        },
                                        "html::-webkit-scrollbar, body::-webkit-scrollbar, *::-webkit-scrollbar": {
                                            display: "none",
                                            width: 0,
                                            height: 0,
                                        },
                                    },
                                }) as any} />
                                
                                <AppMenuResourcesProvider resources={resources}>
                                    <RefineAppContent token={token} resources={resources} Landing={Landing} />
                                    <ConsentSyncTrigger />
                                    <React.Suspense fallback={null}>
                                        <LazyCookieConsentBanner />
                                        <LazyComplianceGateModal />
                                    </React.Suspense>
                                </AppMenuResourcesProvider>
                            </ColorModeContextProvider>
                        </LanguageProvider>
                    </TourProvider>
                </ToastProvider>
            </BrowserRouter>
        </HelmetProvider>
    );
};

export default App;
